/**
 * seedMenuStore — MENU_TREE 정적 트리를 menuStore.setTreeFromBackend 형식으로 변환 후 시드.
 *
 * 다국어: i18n.t(labelKey) 가 매핑 존재 시 적용, 없으면 한국어 default label 사용.
 * 로케일 변경 시 setLocale 후 seedDefaultMenus() 재호출 → 사이드바 즉시 갱신.
 */
import i18n from '@/i18n'
import { useMenuStore } from '@/store/menuStore'
import { useTabStore } from '@/store/tabStore'
import { MENU_TREE, type MenuNode } from '@/layout/menuTree'

interface FlatMenuRow {
  menuId: number
  menuName: string
  labelKey?: string
  parentId: number | null
  menuPath?: string
  icon?: string
  sortOrder: number
}

/** i18n 번들에 labelKey 가 등록되어 있으면 t() 결과, 없으면 한국어 default label. */
const resolveLabel = (n: MenuNode): string => {
  if (!n.labelKey) return n.label
  if (i18n.exists(n.labelKey)) {
    const translated = i18n.t(n.labelKey)
    if (translated && translated !== n.labelKey) return String(translated)
  }
  return n.label
}

/**
 * 트리 구조를 백엔드 메뉴 row 평탄화 포맷(menuId/parentId/sortOrder)으로 변환.
 *  - counter.n: 외부 가변 카운터 — 호출 순서대로 1, 2, 3 ... 부여 (sortOrder 도 동일).
 *  - parentId: null 이면 최상위, 아니면 부모의 menuId.
 */
const flatten = (
  nodes: MenuNode[],
  parentId: number | null,
  counter: { n: number }
): FlatMenuRow[] => {
  const out: FlatMenuRow[] = []
  for (const n of nodes) {
    if (n.hidden) continue
    counter.n += 1
    const myId = counter.n
    out.push({
      menuId: myId,
      menuName: resolveLabel(n),
      labelKey: n.labelKey,
      parentId,
      menuPath: n.path,
      icon: n.icon,
      sortOrder: myId
    })
    if (n.children?.length) out.push(...flatten(n.children, myId, counter))
  }
  return out
}

/**
 * MENU_TREE 를 평탄화 → menuStore 시드 → 탭 라벨 즉시 갱신.
 * 호출 시점: ensureMenusSeeded 최초 호출, 또는 'fw:locale-change' 이벤트 후.
 */
export const seedDefaultMenus = () => {
  const flat = flatten(MENU_TREE, null, { n: 0 })
  useMenuStore.getState().setTreeFromBackend(flat)
  // 탭 라벨도 즉시 갱신 (현재 menuStore.tree 기준)
  const tree = useMenuStore.getState().tree
  const findLabel = (path: string): string => {
    type WalkNode = {
      path?: string
      key?: string
      label?: string
      menuName?: string
      children?: WalkNode[]
    }
    const walk = (nodes: WalkNode[]): string | null => {
      for (const node of nodes) {
        if ((node.path ?? node.key) === path) return String(node.label ?? node.menuName ?? '')
        if (node.children) {
          const r = walk(node.children)
          if (r) return r
        }
      }
      return null
    }
    return walk(tree as WalkNode[]) ?? path
  }
  useTabStore.getState().refreshLabels(findLabel)
}

/** 멱등 시드 — 이미 트리가 존재하면 no-op. RootLayout 인증 가드 직후 호출. */
export const ensureMenusSeeded = () => {
  const tree = useMenuStore.getState().tree
  if (tree.length === 0) seedDefaultMenus()
}

// LOCALE_CHANGE_EVENT 시 자동 reseed (translation bundle 갱신 후)
if (typeof window !== 'undefined') {
  window.addEventListener('fw:locale-change', () => {
    // i18n.addResourceBundle 가 끝난 다음에 실행되도록 microtask 지연
    setTimeout(() => seedDefaultMenus(), 50)
  })
  // FrameworkProvider 가 페이지 첫 mount 시 /labels?locale=xx 응답 적용 후 발행하는 이벤트.
  // 리로드 직후 i18n 번들이 비어있어 seedDefaultMenus 가 ko fallback 으로 시드된 경우,
  // 이 시점에 reseed 하여 사이드바/탭 메뉴 라벨을 실제 locale 로 즉시 교정한다.
  window.addEventListener('fw:labels-loaded', () => {
    setTimeout(() => seedDefaultMenus(), 0)
  })
}
